import { createReducer, on } from '@ngrx/store';
import {
  Workflow,
  WorkflowStep,
  WorkflowStepAction,
  WorkflowStepRule,
  WorkflowStepRuleAction,
  View,
  ViewControlType,
  WorkflowRuleImplementation,
  ViewNames
} from './workflow.model';
import * as WorkflowActions from './workflow.actions';
import { v4 as uuidv4 } from 'uuid';
import produce from 'immer';
import * as authActions from '../../../authentication/store/auth/auth.actions';

export const workflowFeatureKey = 'workflow';

export interface State {
  selectedWorkflow?: Workflow | null;
  selectedWorkflowStep?: WorkflowStep | null;
  selectedWorkflowStepAction?: WorkflowStepAction | null;
  selectedView?: View | null;
  selectedViewName?: string | null;
  selectedWorkflowStepForDelete?: WorkflowStep | null;
  selectedWorkflowStepActionForDelete?: WorkflowStepAction | null;
  selectedViewForDelete?: View | null;
  workflowStepsAfterDelete?: WorkflowStep[];
  stepMigrationUId: string;
  oldStepMigrationUId: string;

  selectedWorkflowStepRule?: WorkflowStepRule | null;
  selectedWorkflowStepRuleForDelete?: WorkflowStepRule | null;
  ruleImplementations?: WorkflowRuleImplementation[];

  err?: any;
  ui?: {
    isDirty?: boolean;
    isStepMigration?: boolean;
    isWorking?: boolean;
    showFormBuilder?: boolean;
    isTransitionFormNew?: boolean;

    isNewRule?: boolean;
    isNewAction?: boolean;
  };
}

export const initialState: State = {
  selectedWorkflow: null,
  selectedWorkflowStep: null,
  selectedWorkflowStepAction: null,
  selectedView: null,
  selectedViewName: null,
  selectedWorkflowStepForDelete: null,
  selectedWorkflowStepActionForDelete: null,
  selectedViewForDelete: null,
  workflowStepsAfterDelete: [],
  stepMigrationUId: null,
  oldStepMigrationUId: null,

  selectedWorkflowStepRule: null,
  selectedWorkflowStepRuleForDelete: null,
  ruleImplementations: [],

  err: null,
  ui: {
    isDirty: false,
    isStepMigration: false,
    isWorking: false,
    showFormBuilder: false,
    isTransitionFormNew: false,

    isNewRule: false,
    isNewAction: false
  }
};

export const reducer = createReducer(
  initialState,

  on(WorkflowActions.setSelectedWorkflow, (state, action) => {
    return {
      ...state,
      err: null,
      selectedWorkflow: action.workflow,
      selectedWorkflowStep: null,
      selectedWorkflowStepAction: null,
      selectedView: null,
      selectedWorkflowStepForDelete: null,
      selectedWorkflowStepActionForDelete: null,
      selectedViewForDelete: null,
      selectedWorkflowStepRule: null,
      ui: {
        ...state.ui,
        isWorking: false,
        isDirty: false,
        showFormBuilder: false,
        isTransitionFormNew: false
      }
    };
  }),
  on(WorkflowActions.getWorkflowForDatasetComplete, (state, action) => {
    let selectedWorkflow = action.workflow;

    const newViews = [];
    const detailView = selectedWorkflow?.views?.find(vw => vw.name === 'detail');
    if (!detailView) {
      newViews.push({
        uId: uuidv4(),
        name: 'detail',
        description: null,
        controls: [],
        configuration: {}
      });
    }
    const tableView = selectedWorkflow?.views?.find(vw => vw.name === 'table');
    if (!tableView) {
      newViews.push({
        uId: uuidv4(),
        name: 'table',
        description: null,
        controls: [],
        configuration: {}
      });
    }

    const cardView = selectedWorkflow?.views?.find(vw => vw.name === 'card');
    if (!cardView) {
      newViews.push({
        uId: uuidv4(),
        name: 'card',
        description: null,
        controls: [],
        configuration: {}
      });
    }

    const popupView = selectedWorkflow?.views?.find(vw => vw.name === 'popup');
    if (!popupView) {
      newViews.push({
        uId: uuidv4(),
        name: 'popup',
        description: null,
        controls: [],
        configuration: {}
      });
    }

    const filterView = selectedWorkflow?.views?.find(vw => vw.name === 'filter');
    if (!filterView) {
      newViews.push({
        uId: uuidv4(),
        name: 'filter',
        description: null,
        controls: [],
        configuration: {}
      });
    }

    const prefsView = selectedWorkflow?.views?.find(vw => vw.name === 'prefs');
    if (!prefsView) {
      newViews.push({
        uId: uuidv4(),
        name: 'prefs',
        description: null,
        controls: [],
        configuration: {}
      });
    }

    if (newViews.length > 0) {
      selectedWorkflow = produce(selectedWorkflow, draftState => {
        if (!!draftState) {
          draftState.views.push(...newViews);
        }
      });
    }

    const skipPagesValidation = action.skipPagesValidation === true;

    return {
      ...state,
      err: null,
      selectedWorkflow,
      selectedWorkflowStep: null,
      selectedWorkflowStepAction: null,
      selectedView: null,
      selectedWorkflowStepForDelete: null,
      selectedWorkflowStepActionForDelete: null,
      selectedViewForDelete: null,
      selectedWorkflowStepRule: null,
      ui: {
        ...state.ui,
        isWorking: false,
        isDirty: !skipPagesValidation && newViews.length > 0 ? true : false,
        showFormBuilder: false,
        isTransitionFormNew: false
      }
    };
  }),
  on(WorkflowActions.setWorkflowStepForDelete, (state, action) => {
    return {
      ...state,
      err: null,
      selectedWorkflowStepForDelete: action.workflowStep
    };
  }),
  on(WorkflowActions.setWorkflowStepMigration, (state, action) => {
    if (state.selectedWorkflowStepForDelete.uId === state.selectedWorkflow.defaultWorkflowStepUId) {
      var newWorkflow: Workflow = {
        ...state.selectedWorkflow,
        defaultWorkflowStepUId: action.stepUId
      };
    } else {
      var newWorkflow: Workflow = state.selectedWorkflow;
    }

    return {
      ...state,
      stepMigrationUId: action.stepUId,
      oldStepMigrationUId: state.selectedWorkflowStepForDelete.uId,
      selectedWorkflow: newWorkflow,
      ui: {
        ...state.ui,
        isStepMigration: true,
        isDirty: true
      }
    };
  }),
  on(WorkflowActions.clearWorkflowStepForDelete, (state, action) => {
    return {
      ...state,
      err: null,
      selectedWorkflowStepForDelete: null,
      workflowStepsAfterDelete: null
    };
  }),
  on(WorkflowActions.setWorkflowStepsForDeleteComplete, (state, action) => {
    return {
      ...state,
      err: null,
      selectedWorkflowStepForDelete: action.deleteStep,
      workflowStepsAfterDelete: action.results
    };
  }),
  on(WorkflowActions.setWorkflowActionForDelete, (state, action) => {
    return {
      ...state,
      err: null,
      selectedWorkflowStep: action.workflowStep,
      selectedWorkflowStepActionForDelete: action.action
    };
  }),
  on(WorkflowActions.clearWorkflowStepActionForDelete, (state, action) => {
    return {
      ...state,
      err: null,
      selectedWorkflowStepActionForDelete: null
    };
  }),
  on(WorkflowActions.deleteWorkflowStep, (state, action) => {
    const { selectedWorkflow, selectedWorkflowStepForDelete } = state;

    const stepIndexToRemove = selectedWorkflow.workflowSteps.findIndex(ws => ws.uId === selectedWorkflowStepForDelete.uId);

    const newWorkflow = produce(selectedWorkflow, draftState => {
      draftState.workflowSteps.splice(stepIndexToRemove, 1);
      for (const step of draftState.workflowSteps) {
        const newActions = step?.actions?.filter(a => a.destinationStepUId !== selectedWorkflowStepForDelete.uId);
        step.actions = newActions;
      }
    });

    return {
      ...state,
      selectedWorkflow: newWorkflow,
      selectedWorkflowStepForDelete: null,
      workflowStepsAfterDelete: null,
      ui: {
        ...state.ui,
        isDirty: true
      }
    };
  }),
  on(WorkflowActions.deleteWorkflowStepAction, (state, action) => {
    const { selectedWorkflow, selectedWorkflowStep, selectedWorkflowStepActionForDelete } = state;

    const newWorkflow = produce(selectedWorkflow, draftState => {
      const step = draftState.workflowSteps.find(s => s.uId === selectedWorkflowStep.uId);
      const actionToRemove = step.actions.findIndex(a => a.uId === selectedWorkflowStepActionForDelete.uId);
      step.actions.splice(actionToRemove, 1);
    });

    return {
      ...state,
      selectedWorkflow: newWorkflow,
      selectedWorkflowStepActionForDelete: null,
      ui: {
        isDirty: true
      }
    };
  }),
  on(WorkflowActions.updateWorkflowStepAction, (state, action) => {
    const { selectedWorkflow } = state;

    const newWorkflow = produce(selectedWorkflow, draftState => {
      const step = draftState.workflowSteps.find(s => s.uId === action.workflowStep.uId);
      const actionIndex = step.actions.findIndex(a => a.uId === action.stepAction.uId);
      step.actions[actionIndex] = action.stepAction;
    });

    return {
      ...state,
      selectedWorkflow: newWorkflow,
      ui: {
        isDirty: true
      }
    };
  }),
  on(WorkflowActions.updateWorkflowStep, (state, action) => {
    const { selectedWorkflow } = state;

    const newWorkflow = produce(selectedWorkflow, draftState => {
      const stepIndex = draftState.workflowSteps.findIndex(ws => ws.uId === action.workflowStep.uId);
      draftState.workflowSteps[stepIndex] = action.workflowStep;
    });

    return {
      ...state,
      selectedWorkflow: newWorkflow,
      ui: {
        isDirty: true
      }
    };
  }),
  on(WorkflowActions.addWorkflowStepAction, (state, action) => {
    const { selectedWorkflow } = state;

    const newWorkflow = produce(selectedWorkflow, draftState => {
      const step = draftState.workflowSteps.find(ws => ws.uId === action.workflowStep.uId);

      const newAction: WorkflowStepAction = {
        uId: uuidv4(),
        sortOrder: step.actions.length + 1,
        name: '',
        description: '',
        iconName: '',
        destinationStepUId: null,
        transitionViewUId: null
      };
      step.actions.push(newAction);
    });

    return {
      ...state,
      selectedWorkflow: newWorkflow,
      ui: {
        isDirty: true
      }
    };
  }),
  on(WorkflowActions.updateWorkflowComplete, (state, action) => {
    return {
      ...state,
      ui: {
        isWorking: false,
        isDirty: false,
        isStepMigration: false
      }
    };
  }),
  on(WorkflowActions.addWorkflowStep, (state, action) => {
    const { selectedWorkflow } = state;

    const newWorkflow = produce(selectedWorkflow, draftState => {
      draftState.workflowSteps.push({
        uId: uuidv4(),
        sortOrder: draftState.workflowSteps.length + 1,
        name: '',
        workflowStepType: 1,
        description: '',
        workflowStatusUId: null,
        statusName: null,
        statusHtmlColorCode: null,
        actions: [],
        rules: [],
        operations: [],
        configuration: { firstStep: false, lastStep: false }
      });
    });

    return {
      ...state,
      selectedWorkflow: newWorkflow,
      ui: {
        isDirty: true
      }
    };
  }),
  on(WorkflowActions.moveWorkflowStep, (state, action) => {
    const { selectedWorkflow } = state;

    const newWorkflow = produce(selectedWorkflow, draftState => {
      const [stepToMove] = draftState.workflowSteps.splice(action.previousIndex, 1);
      draftState.workflowSteps.splice(action.currentIndex, 0, stepToMove);

      let sortOrder = 1;
      for (const stepToRenumber of draftState.workflowSteps) {
        stepToRenumber.sortOrder = sortOrder;
        sortOrder++;
      }
    });

    return {
      ...state,
      selectedWorkflow: newWorkflow,
      ui: {
        isDirty: true
      }
    };
  }),
  on(WorkflowActions.moveWorkflowStepAction, (state, action) => {
    const { selectedWorkflow } = state;

    const newWorkflow = produce(selectedWorkflow, draftState => {
      const step = draftState.workflowSteps.find(ws => ws.uId === action.workflowStep.uId);
      const [actionToMove] = step.actions.splice(action.previousIndex, 1);
      step.actions.splice(action.currentIndex, 0, actionToMove);

      let sortOrder = 1;
      for (const actionToRenumber of step.actions) {
        actionToRenumber.sortOrder = sortOrder;
        sortOrder++;
      }
    });

    return {
      ...state,
      selectedWorkflow: newWorkflow,
      ui: {
        isDirty: true
      }
    };
  }),
  on(WorkflowActions.addEditTransitionForm, (state, action) => {
    const { selectedWorkflow } = state;

    // if showing the form builder ignore this action
    if (state.ui.showFormBuilder) {
      return {
        ...state
      };
    }

    let transitionViewUId = action.stepAction.transitionViewUId;
    let isTransitionFormNew = false;

    const newWorkflow = produce(selectedWorkflow, draftState => {
      const step = draftState.workflowSteps.find(ws => ws.uId === action.workflowStep.uId);
      const stepAction = step.actions.find(a => a.uId === action.stepAction.uId);

      if (!transitionViewUId) {
        // need to create a new view
        isTransitionFormNew = true;

        transitionViewUId = uuidv4();
        const view = {
          uId: transitionViewUId,
          name: '',
          description: '',
          controls: [],
          configuration: {}
        };

        if (!draftState.views) {
          draftState.views = [];
        }
        draftState.views.push(view);

        stepAction.transitionViewUId = transitionViewUId;
      }
    });

    const selectedWorkflowStep = newWorkflow.workflowSteps.find(ws => ws.uId === action.workflowStep.uId);
    const newSelectedView = newWorkflow.views.find(vw => vw.uId === transitionViewUId);

    return {
      ...state,
      selectedWorkflow: newWorkflow,
      selectedWorkflowStep,
      selectedWorkflowStepAction: action.stepAction,
      selectedView: newSelectedView,
      ui: {
        showFormBuilder: true,
        isTransitionFormNew
      }
    };
  }),
  on(WorkflowActions.cancelAddEditTransitionForm, (state, action) => {
    const { selectedWorkflow, selectedWorkflowStep, selectedWorkflowStepAction, selectedView } = state;

    const newWorkflow = produce(selectedWorkflow, draftState => {
      if (state.ui.isTransitionFormNew) {
        // we are "cancel" and we created a new form so remove it
        // from the workflow
        const viewIndex = draftState.views.findIndex(vw => vw.uId === selectedView.uId);
        draftState.views.splice(viewIndex, 1);

        const step = draftState.workflowSteps.find(ws => ws.uId === selectedWorkflowStep.uId);

        const stepAction = step.actions.find(a => a.uId === selectedWorkflowStepAction.uId);

        stepAction.transitionViewUId = null;
      }
    });

    return {
      ...state,
      selectedWorkflow: newWorkflow,
      selectedWorkflowStep: null,
      selectedWorkflowStepAction: null,
      selectedView: null,
      ui: {
        showFormBuilder: false,
        isTransitionFormNew: false
      }
    };
  }),
  on(WorkflowActions.saveAddEditTransitionForm, (state, action) => {
    const { selectedWorkflow, selectedWorkflowStep, selectedWorkflowStepAction, selectedView } = state;
    const newWorkflow = produce(selectedWorkflow, draftState => {
      const viewIndex = draftState.views.findIndex(vw => vw.uId === selectedView.uId);
      draftState.views.splice(viewIndex, 1, action.updatedView);

      if (action.updatedView.controls.length == 0) {
        const viewIndex = draftState.views.findIndex(vw => vw.uId === selectedView.uId);
        draftState.views.splice(viewIndex, 1);
        const step = draftState.workflowSteps.find(ws => ws.uId === selectedWorkflowStep.uId);
        const stepAction = step.actions.find(a => a.uId === selectedWorkflowStepAction.uId);
        stepAction.transitionViewUId = null;
      }
    });

    return {
      ...state,
      selectedWorkflow: newWorkflow,
      selectedWorkflowStep: null,
      selectedWorkflowStepAction: null,
      selectedView: null,
      selectedWorkflowStepForDelete: null,
      selectedWorkflowStepActionForDelete: null,
      selectedViewForDelete: null,
      err: null,
      ui: {
        isDirty: true,
        isWorking: false,
        showFormBuilder: false,
        isTransitionFormNew: false
      }
    };
  }),
  on(WorkflowActions.deleteViewComponent, (state, action) => {
    const { selectedView } = state;

    const newSelectedView = produce(selectedView, draftState => {
      const compIndex = draftState.controls.findIndex(c => c.uId === action.component.uId);
      draftState.controls.splice(compIndex, 1);
    });

    return {
      ...state,
      selectedView: newSelectedView,
      ui: {
        ...state.ui,
        isDirty: true
      }
    };
  }),
  on(WorkflowActions.dragDropComponent, (state, action) => {
    const { selectedView } = state;
    const { previousIndex, currentIndex } = action;

    const newSelectedView = produce(selectedView, draftState => {
      const componentToMove = draftState.controls.splice(previousIndex, 1)[0];
      draftState.controls.splice(currentIndex, 0, componentToMove);

      let index = 1;
      for (const comp of draftState.controls) {
        comp.sortOrder = index;
        index++;
      }
    });

    return {
      ...state,
      selectedView: newSelectedView,
      ui: {
        ...state.ui,
        isDirty: true
      }
    };
  }),
  on(WorkflowActions.addEditDetailsForm, (state, action) => {
    // if already in edit mode then do not process action
    if (state.ui.showFormBuilder) {
      return {
        ...state
      };
    }

    let { selectedWorkflow } = state;
    const selectedWorkflowStep = selectedWorkflow.workflowSteps.find(ws => ws.uId === action.workflowStep.uId);

    const viewName = `edit-${selectedWorkflowStep.uId}`;
    let selectedView = selectedWorkflow?.views?.find(vw => vw.name === viewName);

    if (!selectedView) {
      selectedWorkflow = produce(selectedWorkflow, draftState => {
        const viewUId = uuidv4();
        const newView = {
          uId: viewUId,
          name: viewName,
          description: '',
          controls: [
            {
              uId: uuidv4(),
              viewUId,
              parentViewComponentUId: null,
              sortOrder: 0,
              name: 'Default',
              dataSetFieldUId: '00000000-0000-0000-0000-000000000000',
              controlType: ViewControlType.ViewControlContainer,
              configuration: {},
              controls: []
            }
          ],
          configuration: {}
        };

        draftState.views.push(newView);
      });

      selectedView = selectedWorkflow?.views?.find(vw => vw.name === viewName);
    }

    return {
      ...state,
      selectedWorkflow,
      selectedWorkflowStep,
      selectedWorkflowStepAction: null,
      selectedView,
      ui: {
        showFormBuilder: true,
        isDirty: true
      }
    };
  }),
  on(WorkflowActions.cancelEditDetailsForm, (state, action) => {
    return {
      ...state,
      selectedWorkflowStep: null,
      selectedWorkflowStepAction: null,
      selectedView: null,
      ui: {
        showFormBuilder: false
      }
    };
  }),
  on(WorkflowActions.setIsDirty, (state, action) => {
    return {
      ...state,
      ui: {
        ...state.ui,
        isDirty: action.isDirty
      }
    };
  }),
  on(WorkflowActions.saveAddEditDetailsForm, (state, action) => {
    const { selectedWorkflow, selectedView } = state;

    const newWorkflow = produce(selectedWorkflow, draftState => {
      const viewIndex = draftState.views.findIndex(vw => vw.uId === selectedView.uId);
      draftState.views.splice(viewIndex, 1, action.updatedView);
    });

    return {
      ...state,
      selectedWorkflow: newWorkflow,
      selectedWorkflowStep: null,
      selectedWorkflowStepAction: null,
      selectedView: null,
      selectedWorkflowStepForDelete: null,
      selectedWorkflowStepActionForDelete: null,
      selectedViewForDelete: null,
      err: null,
      ui: {
        isDirty: true,
        isWorking: false,
        showFormBuilder: false
      }
    };
  }),
  on(WorkflowActions.editView, (state, action) => {
    // if already in edit mode then do not process action
    if (state.ui.showFormBuilder) {
      return {
        ...state
      };
    }

    return {
      ...state,
      selectedWorkflowStepAction: null,
      selectedView: action.view,
      selectedViewName: action.name || action.view?.name,
      ui: {
        showFormBuilder: true,
        isDirty: true
      }
    };
  }),
  on(WorkflowActions.moveComponent, (state, action) => {
    const selectedWorkflow = produce(state.selectedWorkflow, draftState => {
      const view = draftState.views.find(vw => vw.uId === action.view.uId);

      const container: any =
        action.view.name === 'detail' ? view.controls.find(c => c.uId === action.selectedViewControlContainerUId) : view;

      const itemToMove = container.controls.splice(action.previousIndex, 1);
      container.controls.splice(action.currentIndex, 0, ...itemToMove);

      let index = 1;
      for (const comp of view.controls) {
        comp.sortOrder = index;
        index++;
      }
    });

    return {
      ...state,
      selectedWorkflow,
      ui: {
        ...state.ui,
        isDirty: true
      }
    };
  }),

  // Rules
  on(WorkflowActions.setSelectedWorkflowStepRule, (state, action) => {
    return {
      ...state,
      selectedWorkflowStep: action.step,
      selectedWorkflowStepRule: action.rule
    };
  }),

  on(WorkflowActions.clearSelectedWorkflowStepRule, (state, action) => {
    return {
      ...state,
      selectedWorkflowStepRule: null
    };
  }),

  on(WorkflowActions.loadWorkflowRuleImplementationsComplete, (state, action) => {
    return {
      ...state,
      ruleImplementations: action.ruleImplementations
    };
  }),

  on(WorkflowActions.setWorkflowStepRuleForDelete, (state, action) => {
    return {
      ...state,
      selectedWorkflowStep: action.step,
      selectedWorkflowStepRuleForDelete: action.rule
    };
  }),

  on(WorkflowActions.clearWorkflowStepRuleForDelete, (state, action) => {
    return {
      ...state,
      selectedWorkflowStepRuleForDelete: null
    };
  }),

  on(WorkflowActions.moveWorkflowStepRule, (state, action) => {
    const { selectedWorkflow } = state;

    const newWorkflow = produce(selectedWorkflow, draftState => {
      const step = draftState.workflowSteps.find(ws => ws.uId === action.workflowStep.uId);
      const [ruleToMove] = step.rules.splice(action.previousIndex, 1);
      step.rules.splice(action.currentIndex, 0, ruleToMove);

      let sortOrder = 0;
      step.rules.map(r => (r.sortOrder = sortOrder++));
    });

    return {
      ...state,
      selectedWorkflow: newWorkflow,
      ui: {
        isDirty: true
      }
    };
  }),

  on(WorkflowActions.setUpdatedWorkflow, (state, action) => {
    return {
      ...state,
      err: null,
      selectedWorkflow: action.workflow,
      selectedWorkflowStep: null,
      selectedWorkflowStepRule: null,
      ui: {
        ...state.ui,
        isDirty: true
      }
    };
  })
);

export const getSelectedWorkflow = (state: State) => state.selectedWorkflow;

export const getSelectedWorkflowStep = (state: State) => state.selectedWorkflowStep;

export const getSelectedWorkflowAction = (state: State) => state.selectedWorkflowStepAction;

export const getSelectedView = (state: State) => state.selectedView;

export const getWorkflowStepForDelete = (state: State) => state.selectedWorkflowStepForDelete;

export const getWorkflowStepActionForDelete = (state: State) => state.selectedWorkflowStepActionForDelete;

export const getSelectedViewForDelete = (state: State) => state.selectedViewForDelete;

export const getIsDirty = (state: State) => state.ui.isDirty;

export const getIsWorking = (state: State) => state.ui.isWorking;

export const getShowFormBuilder = (state: State) => state.ui.showFormBuilder;

export const getDetailView = (state: State) => state?.selectedWorkflow?.views?.find(vw => vw.name === 'detail');

export const getTableView = (state: State) => state?.selectedWorkflow?.views?.find(vw => vw.name === 'table');

export const getCardView = (state: State) => state?.selectedWorkflow?.views?.find(vw => vw.name === 'card');

export const getPopupView = (state: State) => state?.selectedWorkflow?.views?.find(vw => vw.name === 'popup');

export const getFilterView = (state: State) => state?.selectedWorkflow?.views?.find(vw => vw.name === 'filter');

export const getPrefsView = (state: State) => state?.selectedWorkflow?.views?.find(vw => vw.name === 'prefs');

export const getDeleteSteps = (state: State) => state.workflowStepsAfterDelete;

export const getIsStepMigration = (state: State) => state.ui.isStepMigration;

export const getStepMigrationUId = (state: State) => state.stepMigrationUId;

export const getOldStepMigrationUId = (state: State) => state.oldStepMigrationUId;

export const getSelectedWorkflowStepRule = (state: State) => state.selectedWorkflowStepRule;
export const getWorkflowStepRuleForDelete = (state: State) => state.selectedWorkflowStepRuleForDelete;
export const getRuleImplementations = (state: State) => state.ruleImplementations;

export const getWizardViews = (state: State) =>
  state?.selectedWorkflow?.views?.filter(vw => vw.name.startsWith('wizard')).sort((a, b) => (a.name > b.name ? 1 : -1));
export const getViewName = (state: State) => state?.selectedViewName;
